import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { JobStatusBadge } from '@/components/shared/StatusBadges'
import {
  ClientTabSection,
  clientTabFilterOptionClass,
  clientTabFilterTriggerClass,
  clientTabPrimaryBtnClass,
  clientTabSearchInputClass,
  clientTabTableShadow,
  clientTabTdClass,
  clientTabFirstTdClass,
  clientTabThClass,
  clientTabActionsThClass,
  clientTabActionsTdClass,
  clientTabViewDetailsBtnClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientJobs } from '@/hooks/useClients'
import { useListParams } from '@/hooks/useListParams'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { JobStatus, Technician } from '@/types'

const STATUS_OPTIONS: JobStatus[] = [
  'to_plan',
  'planned',
  'assigned',
  'in_progress',
  'completed',
  'cancelled',
]
const ALL = 'all'

type Filters = {
  status?: string
}

const COLUMN_COUNT = 7

function formatTimeSlot(start?: string | null, end?: string | null): string {
  if (!start || !end) return '-'
  return `${start} - ${end}`
}

function technicianName(tech: Technician): string | null {
  return tech.full_name?.trim() || null
}

/** Figma 1230:20180 — show up to two names, then "+N" for the rest. */
function formatAssignees(technicians?: Technician[] | null): string {
  const names =
    technicians?.map(technicianName).filter((n): n is string => Boolean(n)) ??
    []
  if (names.length === 0) return '-'
  if (names.length <= 2) return names.join(', ')
  return `${names.slice(0, 2).join(', ')}, +${names.length - 2}`
}

// 2.4.9 Client details — job history tab.
export function JobHistoryTab({
  clientId,
  canCreateJob = false,
  onOpenJob,
}: {
  clientId: string
  canCreateJob?: boolean
  onOpenJob?: (jobId: string) => void
}) {
  const { t } = useTranslation(['clients', 'enums', 'common'])
  const navigate = useNavigate()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<Filters>()
  const { data, isLoading, isError } = useClientJobs(clientId, query)

  const jobs = data?.data ?? []
  const openJob = (jobId: string) => {
    if (onOpenJob) onOpenJob(jobId)
    else navigate(`/calendar?job=${jobId}`)
  }

  return (
    <ClientTabSection
      title={t('detail.tabs.jobs')}
      toolbar={
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-wrap items-center gap-4'>
            <Select
              value={filters.status ?? ALL}
              onValueChange={v =>
                setFilters({ status: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger className={clientTabFilterTriggerClass}>
                <SelectValue placeholder={t('tabFilters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} className={clientTabFilterOptionClass}>
                  {t('tabFilters.allStatuses')}
                </SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem
                    key={s}
                    value={s}
                    className={clientTabFilterOptionClass}
                  >
                    {t(`enums:jobStatus.${s}`, { defaultValue: s })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <div className='relative min-w-0 flex-1'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('jobs.searchPlaceholder')}
                maxLength={50}
                className={clientTabSearchInputClass}
              />
            </div>
            {canCreateJob && (
              <Button
                className={clientTabPrimaryBtnClass}
                onClick={() => navigate(`/jobs/new?client_id=${clientId}`)}
              >
                <Plus className='size-4' /> {t('jobs.addNew')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div
        className={cn(
          'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
          clientTabTableShadow
        )}
      >
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[1100px] table-fixed caption-bottom text-sm [&_th:nth-child(4)]:text-center [&_td:nth-child(4)]:text-center [&_th:nth-child(6)]:text-center [&_td:nth-child(6)]:text-center'>
            <colgroup>
              <col className='w-[20%]' />
              <col className='w-[15%]' />
              <col className='w-[13%]' />
              <col className='w-[17%]' />
              <col className='w-[15%]' />
              <col className='w-[10%]' />
              <col className='w-[10%]' />
            </colgroup>
            <TableHeader>
              <TableRow className='border-0'>
                <TableHead className={clientTabThClass}>
                  {t('jobs.columns.jobId')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('jobs.columns.scheduledDate')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('jobs.columns.timeSlot')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('jobs.columns.serviceCategory')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('jobs.columns.assignee')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('jobs.columns.status')}
                </TableHead>
                <TableHead className={clientTabActionsThClass}>
                  {t('list.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i} className='border-0'>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={clientTabTdClass}
                    >
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow className='border-0'>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      clientTabTdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('jobs.error')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && jobs.length === 0 && (
                <TableRow className='border-0'>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      clientTabTdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('jobs.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                jobs.map(j => (
                  <TableRow
                    key={j.id}
                    className='group relative cursor-pointer border-0'
                    onClick={() => openJob(j.id)}
                  >
                    <TableCell className={cn(clientTabFirstTdClass)}>
                      <div className='text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'>
                        {j.reference ?? j.id.slice(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {formatDate(j.scheduled_date)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {formatTimeSlot(j.start_time, j.end_time)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {j.service_type ?? '-'}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {formatAssignees(j.assigned_technicians)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <JobStatusBadge
                        status={j.status}
                        label={t(`enums:jobStatus.${j.status}`, {
                          defaultValue: j.status.replace(/_/g, ' '),
                        })}
                        className='capitalize'
                      />
                    </TableCell>
                    <TableCell className={clientTabActionsTdClass}>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation()
                          openJob(j.id)
                        }}
                        className={clientTabViewDetailsBtnClass}
                      >
                        {t('common:actions.view')}
                        <ArrowRight className='size-4 shrink-0' />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='border-t border-[#e7e9ef]'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showShowingRange={false}
        />
      </div>
    </ClientTabSection>
  )
}
